/** Mirrors backend ProgramPeriodConsecutiveActive + ProgramService visibility cascade. */

export interface ProgramPeriodLike {
  id?: string;
  startYear?: string | number;
  endYear?: number;
  startMonth?: string | number;
  endMonth?: string;
  isDefault?: boolean;
  isVisible?: boolean;
}

export function parseProgramMonthNumber(month?: string | number | null): number {
  if (month == null) {
    return 0;
  }
  if (typeof month === 'number' && month >= 1 && month <= 12) {
    return month;
  }
  const numeric = Number(month);
  if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) {
    return numeric;
  }
  const trimmed = String(month).trim();
  const parsed = Date.parse(`${trimmed} 1, 2000`);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).getMonth() + 1;
  }
  const key = trimmed.slice(0, 3).toLowerCase();
  const monthMap: Record<string, number> = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };
  return monthMap[key] ?? 0;
}

function getProgramPeriodEndTicks(program: ProgramPeriodLike): number {
  const em = parseProgramMonthNumber(program.endMonth);
  const endYear = Number(program.endYear) || 0;
  if (em < 1 || endYear <= 0) {
    return Number.MIN_SAFE_INTEGER;
  }
  const sm = parseProgramMonthNumber(program.startMonth);
  const startYear = Number(program.startYear) || 0;
  let resolvedEndYear = endYear;
  if (startYear === endYear && sm >= 1 && sm <= 12 && sm > em) {
    resolvedEndYear = endYear + 1;
  }
  return new Date(resolvedEndYear, em - 1, 1).getTime();
}

function getProgramPeriodStartTicks(program: ProgramPeriodLike): number {
  const sm = parseProgramMonthNumber(program.startMonth);
  const startYear = Number(program.startYear) || 0;
  if (sm < 1 || startYear <= 0) {
    return Number.MIN_SAFE_INTEGER;
  }
  return new Date(startYear, sm - 1, 1).getTime();
}

/** Newest period end first; ties broken by start date descending. */
export function orderProgramsByRecencyDescending<T extends ProgramPeriodLike>(
  programs: T[]
): T[] {
  return [...programs].sort((a, b) => {
    const endDiff = getProgramPeriodEndTicks(b) - getProgramPeriodEndTicks(a);
    if (endDiff !== 0) {
      return endDiff;
    }
    const startDiff =
      getProgramPeriodStartTicks(b) - getProgramPeriodStartTicks(a);
    if (startDiff !== 0) {
      return startDiff;
    }
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
  });
}

/**
 * Apply activate/deactivate cascade to programs in place (rows in the current table).
 * Default program visibility is never modified.
 */
export function applyProgramVisibilityCascade<T extends ProgramPeriodLike>(
  programs: T[],
  anchorId: string,
  anchorIsVisible: boolean
): void {
  const sorted = orderProgramsByRecencyDescending(programs);
  const anchorIdx = sorted.findIndex((p) => p.id === anchorId);
  if (anchorIdx < 0) {
    return;
  }

  const setVisible = (item: T, visible: boolean) => {
    if (item.isDefault) {
      return;
    }
    item.isVisible = visible;
  };

  if (anchorIsVisible) {
    for (let i = 0; i < anchorIdx; i++) {
      const p = sorted[i];
      if (!p.isVisible) {
        setVisible(p, true);
      }
    }
    for (let i = anchorIdx + 1; i < sorted.length; i++) {
      const p = sorted[i];
      if (p.isVisible) {
        setVisible(p, false);
      }
    }
  } else {
    for (let i = anchorIdx + 1; i < sorted.length; i++) {
      const p = sorted[i];
      if (p.isVisible) {
        setVisible(p, false);
      }
    }
  }
}
